import { useState, useEffect, useMemo } from 'react';
import {
  fetchSubmissions,
  fetchVistaJudgments,
  fetchVistaAspects,
  fetchVistaRubrics,
} from 'services/judgmentService';
import {
  mergeJudgmentData,
  transformStrapiDataToTableFormat,
} from 'services/judgmentDataTransformer';
import { getFormSchema } from 'forms';
import { useAuth } from 'contexts/AuthContext';
import { Submission } from 'types/evaluation';
import { useRoleInfo } from 'hooks/useRoleInfo';
import {
  filterBySearchQuery,
  filterByCategory,
  filterByRole,
  sortSubmissions,
} from 'utils/submissionSorting';

const currentYear = new Date().getFullYear();

export const useJudgmentData = () => {
  const [year, setYear] = useState(currentYear);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterStage, setFilterStage] = useState<string>('');
  const [sortField, setSortField] = useState<string>('total_score');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set()
  );

  // Get authenticated user info and role data from localStorage
  useAuth();

  const {
    userRoleType,
    userPillar,
    userCategory,
    userIsSuperAdmin,
    userIsPillarPIC,
    userIsCategoryJudge,
  } = useRoleInfo();

  const userHasAccess = !!userRoleType; // User has access if they have any role

  const loadSubmissions = async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoading(true);
      }
      setError(null);

      const [
        submissionsResponse,
        judgmentsResponse,
        aspectsResponse,
        rubricsResponse,
      ]: any = await Promise.all([
        fetchSubmissions(year),
        fetchVistaJudgments(year),
        fetchVistaAspects(year),
        fetchVistaRubrics(year),
      ]);

      const submissionsData = submissionsResponse.data || submissionsResponse;
      const judgmentsData = judgmentsResponse.data || judgmentsResponse;
      const aspectsData = aspectsResponse.data || aspectsResponse;
      const rubricsData = rubricsResponse.data || rubricsResponse;

      const transformedSubmissions =
        transformStrapiDataToTableFormat(submissionsData);

      const submissionsWithJudgments = mergeJudgmentData(
        transformedSubmissions,
        judgmentsData,
        aspectsData,
        rubricsData
      );

      setSubmissions(submissionsWithJudgments);
    } catch (err: any) {
      console.error('❌ Error loading SMDD judgment data:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name,
      });
      setError(err.message || String(err));
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadSubmissions();
  }, [year]);

  const handleRefresh = async (isBackground = false) => {
    await loadSubmissions(isBackground === true);
  };

  const updateSubmissionStageLocal = (
    documentId: string,
    newStage: string,
    updatedLogs?: any[]
  ) => {
    setSubmissions(prev =>
      prev.map(s =>
        s.documentId === documentId
          ? {
              ...s,
              competition_stage: newStage,
              stage_action_logs: updatedLogs || s.stage_action_logs,
            }
          : s
      )
    );
  };

  const toggleRowExpansion = (id: string | number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const schema = getFormSchema('smdd');
  const categories = schema.categories;

  const filteredAndSortedSubmissions = useMemo(() => {
    // 1. Text search filter
    const afterSearch = searchQuery
      ? filterBySearchQuery(submissions, searchQuery)
      : submissions;

    // 2. Category dropdown filter (skip if user is already scoped to a category)
    const afterCategory =
      filterCategory && !userIsCategoryJudge
        ? filterByCategory(afterSearch, filterCategory)
        : afterSearch;

    // 2b. Stage filter
    const afterStage = filterStage
      ? afterCategory.filter(s => {
          const stage = s.competition_stage || 'preselection';
          return stage === filterStage;
        })
      : afterCategory;

    // 3. Role-based access control
    const afterRole = filterByRole(afterStage, {
      userIsSuperAdmin,
      userIsPillarPIC,
      userIsCategoryJudge,
      userPillar,
      userCategory,
    });

    // 4. Sort
    return sortSubmissions(afterRole, submissions, sortField, sortDirection);
  }, [
    submissions,
    filterCategory,
    filterStage,
    searchQuery,
    sortField,
    sortDirection,
    userIsSuperAdmin,
    userIsPillarPIC,
    userIsCategoryJudge,
    userPillar,
    userCategory,
  ]);

  const stageOptions = useMemo(() => {
    return [
      { value: 'preselection', label: 'PreSelection' },
      { value: 'qualification', label: 'Qualification' },
      { value: 'final', label: 'Final' },
    ];
  }, []);

  const stats = useMemo(() => {
    const baseSubmissions = filteredAndSortedSubmissions;

    const categoryFilteredSubmissions =
      filterCategory && !userIsCategoryJudge
        ? baseSubmissions.filter(s => s.category === filterCategory)
        : baseSubmissions;

    const scoredSubmissions = categoryFilteredSubmissions.filter(
      s => s.total_score !== null && s.total_score !== undefined
    );

    const averageScore =
      scoredSubmissions.length > 0
        ? scoredSubmissions.reduce((sum, s) => sum + s.total_score, 0) /
          scoredSubmissions.length
        : 0;

    const highestScore =
      scoredSubmissions.length > 0
        ? Math.max(...scoredSubmissions.map(s => s.total_score))
        : 0;

    const statsResult = {
      total: categoryFilteredSubmissions.length,
      scored: scoredSubmissions.length,
      pending: categoryFilteredSubmissions.length - scoredSubmissions.length,
      averageScore: Math.round(averageScore * 100) / 100,
      highestScore: highestScore.toFixed(2),
      categoryName: filterCategory || 'All Categories',
    };

    return statsResult;
  }, [submissions, filterCategory, userIsCategoryJudge]);

  const getMainDescription = (submission: Submission) => {
    if (submission.project_name) return submission.project_name;
    if (submission.leadership_achievement)
      return submission.leadership_achievement;
    return 'No description available';
  };

  const sanitizeAIText = (text: string | null | undefined) => {
    if (!text) return '';

    return text
      .replace(/^[\s]*[•·\-*→►‣⁃◦▪▫‒–—]+[\s]*/gm, '')
      .replace(/^[\s]*\d+[.)]\s*/gm, '')
      .replace(/^[\s]*[a-zA-Z][.)]\s*/gm, '')
      .trim();
  };

  return {
    submissions,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filterCategory,
    setFilterCategory,
    filterStage,
    setFilterStage,
    allStages: stageOptions,
    year,
    setYear,
    sortField,
    sortDirection,
    expandedRows,
    handleRefresh,
    updateSubmissionStageLocal,
    toggleRowExpansion,
    handleSort,
    categories,
    filteredAndSortedSubmissions,
    stats,
    getMainDescription,
    sanitizeAIText,
    // Access control
    userHasAccess,
    userPillar,
    userIsSuperAdmin,
    // Role information
    userRoleType,
    userCategory,
    userIsPillarPIC,
    userIsCategoryJudge,
  };
};
